import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  TrendingUp, 
  Target, 
  Activity, 
  BarChart3,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

interface CycleStats {
  totalCycles: number;
  averageCycleLength: number;
  cycleVariability: number;
  averagePeriodLength: number;
  mostCommonSymptoms: Array<{ symptom: string; frequency: number; percentage: number }>;
  moodPatterns: Record<string, number>;
  flowPatterns: Record<string, number>;
  phaseAnalysis: {
    menstrual: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
    follicular: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
    ovulation: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
    luteal: { avgLength: number; commonSymptoms: string[]; commonMoods: string[] };
  };
  predictionAccuracy: number;
  trackingConsistency: number;
}

interface CycleStatsOverviewProps {
  stats: CycleStats;
}

const CycleStatsOverview: React.FC<CycleStatsOverviewProps> = ({ stats }) => {
  const getRegularityStatus = () => {
    if (stats.cycleVariability <= 2) return { status: 'Excellent', color: 'text-green-600', bg: 'bg-green-100' };
    if (stats.cycleVariability <= 5) return { status: 'Good', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (stats.cycleVariability <= 8) return { status: 'Moderate', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { status: 'Variable', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const getTrackingStatus = () => {
    if (stats.trackingConsistency >= 80) return { status: 'Excellent', color: 'text-green-600', bg: 'bg-green-100' };
    if (stats.trackingConsistency >= 60) return { status: 'Good', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (stats.trackingConsistency >= 40) return { status: 'Fair', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { status: 'Needs Improvement', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const regularityStatus = getRegularityStatus();
  const trackingStatus = getTrackingStatus();

  const overviewCards = [
    {
      title: 'Total Cycles Tracked',
      value: stats.totalCycles.toString(),
      subtitle: 'Complete cycles',
      icon: Calendar,
      color: 'from-purple-500 to-indigo-500'
    },
    {
      title: 'Average Cycle Length',
      value: `${stats.averageCycleLength} days`,
      subtitle: `±${stats.cycleVariability.toFixed(1)} days variation`,
      icon: TrendingUp,
      color: 'from-blue-500 to-cyan-500',
      status: regularityStatus
    },
    {
      title: 'Prediction Accuracy',
      value: `${stats.predictionAccuracy}%`,
      subtitle: 'Based on your data',
      icon: Target,
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Tracking Consistency',
      value: `${stats.trackingConsistency}%`,
      subtitle: 'Last 3 months',
      icon: CheckCircle,
      color: 'from-pink-500 to-rose-500',
      status: trackingStatus
    }
  ];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {overviewCards.map((card, index) => (
          <motion.div
            key={card.title}
            className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -2, scale: 1.02 }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <p className="text-xs font-medium text-gray-500 mb-1">{card.title}</p>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{card.value}</h3>
                <p className="text-xs text-gray-600">{card.subtitle}</p>
              </div>
              <motion.div
                className={`p-2 bg-gradient-to-br ${card.color} rounded-lg`}
                whileHover={{ rotate: 10, scale: 1.1 }}
              >
                <card.icon className="w-4 h-4 text-white" />
              </motion.div>
            </div>
            
            {card.status && (
              <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${card.status.bg} ${card.status.color}`}>
                {card.status.status === 'Excellent' && <CheckCircle className="w-3 h-3 mr-1" />}
                {card.status.status.includes('Needs') && <AlertTriangle className="w-3 h-3 mr-1" />}
                {!card.status.status.includes('Excellent') && !card.status.status.includes('Needs') && <Clock className="w-3 h-3 mr-1" />}
                {card.status.status}
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Symptom Patterns */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-6"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center space-x-2 mb-4">
            <Activity className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Top Symptoms</h3>
          </div>
          
          {stats.mostCommonSymptoms.length > 0 ? (
            <div className="space-y-3">
              {stats.mostCommonSymptoms.slice(0, 5).map((symptom, index) => (
                <motion.div
                  key={symptom.symptom}
                  className="flex items-center justify-between"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                >
                  <span className="text-sm text-gray-700 capitalize">
                    {symptom.symptom.replace('_', ' ')}
                  </span>
                  <div className="flex items-center space-x-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <motion.div
                        className="bg-purple-500 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${symptom.percentage}%` }}
                        transition={{ delay: 0.7 + index * 0.1, duration: 0.8 }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-8">{symptom.percentage}%</span>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No symptom data available yet</p>
          )}
        </motion.div>

        {/* Mood Distribution */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-white/20 p-6"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex items-center space-x-2 mb-4">
            <BarChart3 className="w-5 h-5 text-pink-600" />
            <h3 className="text-lg font-semibold text-gray-900">Mood Patterns</h3>
          </div>
          
          {Object.keys(stats.moodPatterns).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(stats.moodPatterns)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 5)
                .map(([mood, count], index) => {
                  const total = Object.values(stats.moodPatterns).reduce((sum, c) => sum + c, 0);
                  const percentage = Math.round((count / total) * 100);
                  
                  return (
                    <motion.div
                      key={mood}
                      className="flex items-center justify-between"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 + index * 0.1 }}
                    >
                      <span className="text-sm text-gray-700 capitalize">{mood}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <motion.div
                            className="bg-pink-500 h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-8">{percentage}%</span>
                      </div>
                    </motion.div>
                  );
                })}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No mood data available yet</p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default CycleStatsOverview;